import { omit } from 'lodash-es';
import { NextPage } from 'next';
import { useTranslation } from 'next-i18next/pages';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { Flavor } from '@pflegia/types/common/enums/flavor.enum';
import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import { HOME } from '@pflegia/ui/pflegia/constants/routes';
import { toStripSlugs } from '@pflegia/ui/shared/constants/navigation';
import Spinner from '@pflegia/ui/shared/spinner';
import { useToast } from '@pflegia/ui/shared/toaster/ToastProvider';

import NextSeo from '@shared/components/NextSeo';
import { getFlavorFromHost } from '@shared/utils/get-flavor';
import { createStaticPropsWithTranslations, getFlavorPaths } from '@shared/utils/static-paths';

const GuestLandingPage = dynamic(() => import('@domains/landing-page/GuestLandingPage'));
const PraxiaLandingPage = dynamic(() => import('@domains/landing-page/PraxiaLandingPage'));

const Home: NextPage = () => {
  const { t } = useTranslation('landingPage');
  const router = useRouter();
  const toast = useToast();
  const hasShownToast = useRef(false);
  const isInvalidFlavor = router.isReady && ![Flavor.Pflegia, Flavor.Praxia].includes(router.query.flavor as Flavor);

  useEffect(() => {
    if (router.query.invalidMagicLink === 'true' && !hasShownToast.current) {
      toast?.error(t('invalidMagicLinkToast.invalidLink'));
      hasShownToast.current = true;
      const { pathname, query } = router;
      const pathnameWithoutQuery = router.asPath.split('?')[0] ?? HOME;
      router.replace({ pathname, query }, { pathname: pathnameWithoutQuery, query: omit(query, toStripSlugs) }, { shallow: true });
    }
  }, [router, t, toast]);

  // A flavorless canonical URL (e.g. /funnel) can be mis-resolved client-side to this [flavor] route
  // with the path segment as the flavor. Re-route to the real /[flavor]/... page (flavor from host)
  // while keeping the canonical URL as the displayed path, so it resolves without a full reload
  useEffect(() => {
    if (!isInvalidFlavor) return;
    const flavor = getFlavorFromHost(window.location.host);
    router.replace(`/${flavor}${router.asPath}`, router.asPath);
  }, [isInvalidFlavor, router]);

  const { isPraxia } = useFlavorFromRoute();

  if (isInvalidFlavor) return <Spinner />;

  return (
    <>
      <NextSeo description={t('seo.description')} title={t(isPraxia ? 'seo.praxia.title' : 'seo.title')} />
      {isPraxia ? <PraxiaLandingPage /> : <GuestLandingPage />}
    </>
  );
};

export const getStaticPaths = getFlavorPaths;
export const getStaticProps = createStaticPropsWithTranslations(['common', 'candidateFields', 'jobTitles', 'landingPage', 'widgets']);

export default Home;
